import { useState, useEffect, useRef } from 'react';
import { Decimal, ZERO } from '@insio/decimal';
import type { IDocumentExchangeRate } from '@insio/shared-types';
import type { EditorProps } from '../types/editor-props';
import { resolveExchangeRateValue } from '../../../forms/invoice-totals/resolve-exchange-rate-value';
import {
  InvoiceTotalsForm,
  type InvoiceTotalsFormData
} from '../../../forms/invoice-totals/InvoiceTotalsForm';
import {
  useInvoiceTotalsFormState,
  useSuggestedExchangeRate
} from '../../../forms/invoice-totals/hooks';
import type { InvoiceTotalsPaymentStatus } from '../../../modals/EditInvoiceTotalsModal';
import { InlineEditorActions } from './InlineEditorActions';
import { useTranslation } from '../../../../providers/UIProvider';
import {
  useInvoiceAccountingCurrencyCode,
  useInvoiceCurrencyCode
} from '../../../../providers/InvoiceProvider';

interface InvoiceTotalsValue {
  totalAmountNoVat?: Decimal | null;
  totalVatAmount?: Decimal | null;
  totalAmountInclVat?: Decimal | null;
  paymentStatus?: InvoiceTotalsPaymentStatus | null;
  paidAmount?: Decimal | null;
  accountingExchangeRate?: IDocumentExchangeRate | null;
  roundingAmount?: Decimal | null;
  accountingAmount?: Decimal | null;
  accountingAmountNoVat?: Decimal | null;
  accountingTotalVatAmount?: Decimal | null;
  accountingCurrencyCode?: string | null;
}

function toDecimalOrZero(value: Decimal | null | undefined): Decimal {
  return value instanceof Decimal ? value : ZERO;
}

/**
 * Inline editor for invoice totals; the paid status and amount are shown, never
 * edited or saved. Renders the form directly in-place (no modal). Intended for
 * the ScanEditor review panel where a dialog would be disruptive.
 */
export function InvoiceTotalsInlineEditor({
  value,
  onChange,
  onCommit,
  onCancel,
  errors,
  onRegisterSave,
  onDraftChange
}: EditorProps) {
  const t = useTranslation('modules.modals.editInvoiceTotals');
  const invoiceCurrencyCode = useInvoiceCurrencyCode();
  const accountingCurrencyCode = useInvoiceAccountingCurrencyCode();
  const [saving, setSaving] = useState(false);

  const data = (value ?? {}) as InvoiceTotalsValue;

  const currenciesDiffer =
    accountingCurrencyCode != null &&
    invoiceCurrencyCode != null &&
    accountingCurrencyCode !== invoiceCurrencyCode;
  const showAccountingExchangeRate = currenciesDiffer || data.accountingExchangeRate != null;

  const state = useInvoiceTotalsFormState({
    open: true,
    currentTotalNoVat: toDecimalOrZero(data.totalAmountNoVat),
    currentTotalVatAmount: toDecimalOrZero(data.totalVatAmount),
    currentTotalInclVat: toDecimalOrZero(data.totalAmountInclVat),
    currentAccountingExchangeRate: data.accountingExchangeRate?.rate ?? null,
    currentRoundingAmount: data.roundingAmount,
    currentAccountingAmount: data.accountingAmount,
    currentAccountingAmountNoVat: data.accountingAmountNoVat,
    currentAccountingTotalVatAmount: data.accountingTotalVatAmount,
    currentAccountingCurrencyCode: data.accountingCurrencyCode ?? accountingCurrencyCode,
    documentCurrencyCode: invoiceCurrencyCode
  });
  const suggestedAccountingExchangeRate = useSuggestedExchangeRate(
    state.accountingCurrencyCode,
    state.currenciesSame
  );

  const stateWithDraftChange = {
    ...state,
    setRawNoVat: (value: string) => {
      state.setRawNoVat(value);
      onDraftChange?.();
    },
    setRawVatAmount: (value: string) => {
      state.setRawVatAmount(value);
      onDraftChange?.();
    },
    setRawInclVat: (value: string) => {
      state.setRawInclVat(value);
      onDraftChange?.();
    },
    setRawAccountingExchangeRate: (value: string) => {
      state.setRawAccountingExchangeRate(value);
      onDraftChange?.();
    },
    setRawRoundingAmount: (value: string) => {
      state.setRawRoundingAmount(value);
      onDraftChange?.();
    },
    setRawAccountingAmountNoVat: (value: string) => {
      state.setRawAccountingAmountNoVat(value);
      onDraftChange?.();
    },
    setRawAccountingTotalVatAmount: (value: string) => {
      state.setRawAccountingTotalVatAmount(value);
      onDraftChange?.();
    },
    setRawAccountingAmount: (value: string) => {
      state.setRawAccountingAmount(value);
      onDraftChange?.();
    },
    setAccountingCurrencyCode: (code: string | null) => {
      state.setAccountingCurrencyCode(code);
      onDraftChange?.();
    },
    recomputeAccounting: () => {
      state.recomputeAccounting();
      onDraftChange?.();
    },
    applyAccountingExchangeRate: (value: Decimal) => {
      state.applyAccountingExchangeRate(value);
      onDraftChange?.();
    }
  };

  const handleSave = async () => {
    const {
      finalNoVat,
      finalVatAmount,
      finalInclVat,
      finalAccountingExchangeRate,
      finalRoundingAmount,
      finalAccountingAmount,
      finalAccountingAmountNoVat,
      finalAccountingTotalVatAmount,
      finalAccountingCurrencyCode
    } = state.flushAndGet();
    setSaving(true);
    try {
      const next: InvoiceTotalsFormData = {
        totalAmountNoVat: finalNoVat,
        totalVatAmount: finalVatAmount,
        totalAmountInclVat: finalInclVat,
        accountingExchangeRate: resolveExchangeRateValue(
          finalAccountingExchangeRate,
          data.accountingExchangeRate,
          suggestedAccountingExchangeRate
        ),
        roundingAmount: finalRoundingAmount,
        accountingAmount: finalAccountingAmount,
        accountingAmountNoVat: finalAccountingAmountNoVat,
        accountingTotalVatAmount: finalAccountingTotalVatAmount,
        accountingCurrencyCode: finalAccountingCurrencyCode
      };
      if (onCommit) {
        await onCommit(next);
      } else {
        onChange(next);
      }
    } catch (err) {
      state.setError(err instanceof Error ? err.message : t('errors.saveError'));
      if (onRegisterSave) throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
  };

  // Register handleSave with the ScanEditor global save coordinator.
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  useEffect(() => {
    if (!onRegisterSave) return;
    onRegisterSave(() => handleSaveRef.current());
    return () => onRegisterSave(null);
  }, [onRegisterSave]);

  return (
    <div className='flex flex-col gap-sm w-full'>
      <InvoiceTotalsForm
        state={stateWithDraftChange}
        currencyCode={invoiceCurrencyCode}
        accountingCurrencyCode={accountingCurrencyCode}
        paymentStatus={data.paymentStatus ?? null}
        paidAmount={data.paidAmount ?? null}
        showAccountingExchangeRate={showAccountingExchangeRate}
        suggestedAccountingExchangeRate={suggestedAccountingExchangeRate}
        disabled={saving}
        errors={errors}
      />
      {!onRegisterSave && (
        <InlineEditorActions t={t} saving={saving} onCancel={handleCancel} onSave={handleSave} />
      )}
    </div>
  );
}

InvoiceTotalsInlineEditor.displayName = 'InvoiceTotalsInlineEditor';
